import clsx from 'clsx';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { Button, Image, renderIcon } from '@gitbook/spine-react';
import { ZIP_MIME_TYPES } from '@gitbook/util-files';

import { useMediaGallery } from '../MediaGallery';
import { Indicator } from './EditorContextSummary';

type AttachmentsSummarySize = 'small' | 'large';

type AttachmentItemProps<T extends api.AIAttachment> = {
    attachment: T;
    size: AttachmentsSummarySize;
    onRemove?: () => void;
};

/**
 * Display the attachments to a message.
 */
export function AttachmentsSummary(props: {
    /**
     * Attachments to display.
     */
    attachments: api.AIAttachment[];
    /**
     * Size of the attachments.
     */
    size?: AttachmentsSummarySize;
    /**
     * Optional callback when the list is editable
     */
    onUpdateAttachments?: (attachments: api.AIAttachment[]) => void;
    /**
     * Optional callback to remove a single attachment by index.
     * Takes precedence over `onUpdateAttachments` for the remove action, useful when
     * the displayed attachments are a preview of another source (e.g. local files).
     */
    onRemoveAttachment?: (index: number) => void;
    /**
     * Class name to apply to the container.
     */
    className?: string;
    /**
     * Whether the attachments should wrap to multiple lines if they exceed the container width. Defaults to true.
     */
    wraps?: boolean;
}) {
    const {
        attachments,
        onUpdateAttachments,
        onRemoveAttachment,
        size = 'small',
        className,
        wraps = true,
    } = props;

    return (
        <div
            className={clsx('flex flex-row gap-2', wraps ? 'flex-wrap' : 'flex-nowrap', className)}
        >
            {attachments.map((attachment, index) => (
                <AttachmentItem
                    key={index}
                    attachment={attachment}
                    size={size}
                    onRemove={
                        onRemoveAttachment
                            ? () => onRemoveAttachment(index)
                            : onUpdateAttachments
                              ? () => onUpdateAttachments(attachments.filter((_, i) => i !== index))
                              : undefined
                    }
                />
            ))}
        </div>
    );
}

function AttachmentItem(props: AttachmentItemProps<api.AIAttachment>) {
    const { attachment } = props;

    switch (attachment.type) {
        case 'file':
        case 'import-file':
            return <AttachmentItemFile {...props} attachment={attachment} />;
        case 'image':
            return <AttachmentItemImage {...props} attachment={attachment} />;
        default:
            return null;
    }
}

/**
 * Pick an icon representing a file attachment from its content type.
 */
function getFileAttachmentIcon(contentType: string) {
    if (contentType === 'application/pdf') {
        return Icon.PDF;
    }
    if (ZIP_MIME_TYPES.includes(contentType)) {
        return Icon.FileZIP;
    }
    if (/word|msword|opendocument\.text|rtf|\.pages$/.test(contentType)) {
        return Icon.FileWord;
    }
    if (/excel|spreadsheet|csv|tsv|iif/.test(contentType)) {
        return Icon.Table;
    }
    if (
        /json|xml|javascript|typescript|html|graphql|yaml|toml|^text\/x-|^application\/x-/.test(
            contentType
        )
    ) {
        return Icon.FileCode;
    }
    return Icon.FileText;
}

function AttachmentItemFile(
    props: AttachmentItemProps<api.AIAttachmentFile | api.AIAttachmentImportFile>
) {
    const { attachment, onRemove } = props;
    return (
        <Indicator
            label={attachment.filename}
            icon={renderIcon(getFileAttachmentIcon(attachment.contentType), {
                className: 'size-3.5 text-secondary',
            })}
            onPress={() => {
                window.open(attachment.url, '_blank');
            }}
            action={
                onRemove ? (
                    <Button
                        kind="blank"
                        size="small"
                        iconOnly
                        label="Remove"
                        leadingIcon={Icon.ClearCircle}
                        onPress={onRemove}
                    />
                ) : null
            }
        />
    );
}

function AttachmentItemImage(props: AttachmentItemProps<api.AIAttachmentImage>) {
    const { attachment, onRemove } = props;
    const mediaGallery = useMediaGallery();
    return (
        <Indicator
            label={attachment.filename ?? 'Image'}
            icon={<Image src={attachment.url} className="size-4 rounded-xs object-cover" />}
            onPress={() => {
                mediaGallery.open([
                    {
                        url: attachment.url,
                    },
                ]);
            }}
            action={
                onRemove ? (
                    <Button
                        kind="blank"
                        size="small"
                        iconOnly
                        label="Remove"
                        leadingIcon={Icon.ClearCircle}
                        onPress={onRemove}
                    />
                ) : null
            }
        />
    );
}
